/**
 * Client-side usage telemetry (2026-10-08) — page views, session starts and named feature uses,
 * posted to `POST /api/v1/usage/events` and stored in `[Dra].[AppUsageEvent]` with Host 'dra',
 * where `reporting.dolderapps.com/app-usage` reads them. The fourth event type, `api_call`, is
 * recorded on the server (`api/src/shared/usage-tracker.ts`).
 *
 * Adapted from `customer360-app/web/src/services/usage.ts` (itself from `reporting_app`), with
 * the DRA's two differences:
 *
 *  - **No token.** Same-origin and relative, like every call in `services/api.ts`: the SWA
 *    EasyAuth cookie rides along and the gateway turns it into `x-ms-client-principal`.
 *  - **Several screens.** A path is reduced to its ROUTE before it is queued — `/tickets/DRA-…`
 *    becomes `/tickets/:ticketNumber`, the query string (where `?search=` lives) is never read,
 *    and a path that matches no route is not reported at all. The server applies the same rule
 *    again; this side keeps the content from ever leaving the browser.
 *
 * Batches client-side (cap 200, drop-oldest), flushed every 10 s and on visibilitychange /
 * pagehide via `fetch` keepalive (max 25 events per batch — keepalive bodies are limited to
 * 64 KB). A failed batch is retried once on the next flush, then dropped. Every flush swallows its
 * own errors: telemetry never surfaces as an app error. Off in `vite dev` — local `func start`
 * points at the production DolderDB, and a developer's clicks are not usage.
 */
import { matchPath } from 'react-router-dom';

interface QueuedEvent {
  type: 'page_view' | 'session_start' | 'feature';
  page?: string;
  feature?: string;
  ts: number;
  meta?: Record<string, number | boolean>;
  retried?: boolean;
}

/**
 * Every route in `App.tsx`, as a pattern. A test reads `App.tsx` and fails when a route is
 * added there and not here — a screen missing from this list would simply never be counted.
 */
export const USAGE_ROUTES = [
  '/tickets',
  '/tickets/:ticketNumber',
  '/review',
  '/approvals',
  '/authorizations',
  '/masters',
  '/audit-trail',
  '/settings',
] as const;

const ENDPOINT = '/api/v1/usage/events';
const DISABLED = import.meta.env.DEV;
const SESSION_KEY = 'dra_usage_session_id';
const FLUSH_INTERVAL_MS = 10_000;
const MAX_BATCH = 25;
const MAX_QUEUE = 200;
const PAGE_VIEW_DEDUPE_MS = 500;

let queue: QueuedEvent[] = [];
let sessionId: string | null = null;
let initialized = false;
let flushInFlight = false;
let lastPageViewPath = '';
let lastPageViewAt = 0;

/** The route pattern a browser path belongs to, or `null` when it is no screen of this app. */
export function routeOf(pathname: string): string | null {
  for (const pattern of USAGE_ROUTES) {
    if (matchPath({ path: pattern, end: true }, pathname)) return pattern;
  }
  return null;
}

/** `/tickets/:ticketNumber` → `ticket`, `/audit-trail` → `audit-trail`: the feature-name prefix. */
export function pageKeyOf(route: string | null): string {
  if (route === '/tickets/:ticketNumber') return 'ticket';
  return route ? route.slice(1) : 'app';
}

function ensureInit(): void {
  if (initialized || DISABLED) return;
  initialized = true;

  try {
    sessionId = sessionStorage.getItem(SESSION_KEY);
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, sessionId);
      enqueue({ type: 'session_start', ts: Date.now() });
    }
  } catch {
    sessionId = null;
  }

  window.setInterval(() => { void flush(); }, FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
  window.addEventListener('pagehide', () => { void flush(); });
}

function enqueue(event: QueuedEvent): void {
  if (queue.length >= MAX_QUEUE) queue.shift();
  queue.push(event);
}

async function flush(): Promise<void> {
  if (flushInFlight || queue.length === 0) return;
  flushInFlight = true;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(batch.length);
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        events: batch.map(({ retried: _r, ...e }) => e),
      }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } catch {
    // One retry on the next flush, then drop — never spam, never grow.
    const retryable = batch.filter(e => !e.retried).map(e => ({ ...e, retried: true }));
    queue = [...retryable, ...queue].slice(0, MAX_QUEUE);
  } finally {
    flushInFlight = false;
  }
}

/** One page view per route change. Called by `<UsageTracker />` in `App.tsx`. */
export function trackPageView(pathname: string): void {
  if (DISABLED) return;
  const page = routeOf(pathname);
  if (!page) return;
  ensureInit();
  const now = Date.now();
  if (page === lastPageViewPath && now - lastPageViewAt < PAGE_VIEW_DEDUPE_MS) return;
  lastPageViewPath = page;
  lastPageViewAt = now;
  enqueue({ type: 'page_view', page, ts: now });
}

/**
 * A UI-only interaction the server cannot see (a CSV export, a step through the review queue,
 * a column shown or hidden). Recorded as `<page-key>.<action>`, e.g. `review.csv-export`.
 * `meta` takes counts and flags only — the server drops anything else, and so should a caller.
 */
export function trackFeature(action: string, meta?: Record<string, number | boolean>): void {
  if (DISABLED || !action) return;
  ensureInit();
  const page = routeOf(window.location.pathname);
  enqueue({
    type: 'feature',
    feature: `${pageKeyOf(page)}.${action}`.slice(0, 100),
    ...(page ? { page } : {}),
    ts: Date.now(),
    meta,
  });
}
